import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useSettings } from '../../contexts/SettingsContext';
import type { SystemSetting } from '../../types/settings';

const systemSettingSchema = z.object({
  key: z.string().min(1, 'Key is required'),
  value: z.string().min(1, 'Value is required'),
  category: z.string().min(1, 'Category is required'),
  description: z.string().optional(),
  is_public: z.boolean().default(false)
});

type SystemSettingFormData = z.infer<typeof systemSettingSchema>;

export default function SystemSettingsForm() {
  const { state, updateSystemSetting, addSystemSetting, deleteSystemSetting } = useSettings();
  const [editingSetting, setEditingSetting] = useState<SystemSetting | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm<SystemSettingFormData>({
    resolver: zodResolver(systemSettingSchema),
    defaultValues: {
      key: '',
      value: '',
      category: '',
      description: '',
      is_public: false
    }
  });

  const handleEdit = (setting: SystemSetting) => {
    setEditingSetting(setting);
    reset({
      key: setting.key,
      value: typeof setting.value === 'string' ? setting.value : JSON.stringify(setting.value),
      category: setting.category,
      description: setting.description || '',
      is_public: setting.is_public
    });
    setIsAdding(false);
  };

  const handleAdd = () => {
    setEditingSetting(null);
    reset({
      key: '',
      value: '',
      category: '',
      description: '',
      is_public: false
    });
    setIsAdding(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this setting?')) {
      try {
        await deleteSystemSetting(id);
      } catch (error) {
        console.error('Error deleting setting:', error);
      }
    }
  };

  const onSubmit = async (data: SystemSettingFormData) => {
    try {
      // Try to parse the value as JSON if it looks like JSON
      let parsedValue: any = data.value;
      if (data.value.trim().startsWith('{') || data.value.trim().startsWith('[')) {
        try {
          parsedValue = JSON.parse(data.value);
        } catch (e) {
          // If parsing fails, use the string value
          console.warn('Failed to parse value as JSON:', e);
        }
      }

      if (editingSetting) {
        await updateSystemSetting({
          ...editingSetting,
          key: data.key,
          value: parsedValue,
          category: data.category,
          description: data.description,
          is_public: data.is_public
        });
      } else {
        await addSystemSetting({
          key: data.key,
          value: parsedValue,
          category: data.category,
          description: data.description,
          is_public: data.is_public
        });
      }
      
      setEditingSetting(null);
      setIsAdding(false);
      reset();
    } catch (error) {
      console.error('Error saving setting:', error);
    }
  };

  const cancelEdit = () => {
    setEditingSetting(null);
    setIsAdding(false);
    reset();
  };

  // Group settings by category
  const groupedSettings = state.systemSettings.reduce((acc, setting) => {
    if (!acc[setting.category]) {
      acc[setting.category] = [];
    }
    acc[setting.category].push(setting);
    return acc;
  }, {} as Record<string, SystemSetting[]>);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-golf-gray">System Settings</h2>
        <button
          onClick={handleAdd}
          className="flex items-center space-x-2 px-4 py-2 bg-golf-orange text-white rounded-lg hover:bg-golf-orange/90 transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          <span>Add Setting</span>
        </button>
      </div>

      {(isAdding || editingSetting) && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-golf-gray mb-4">
            {editingSetting ? 'Edit Setting' : 'Add New Setting'}
          </h3>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Key <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('key')}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                />
                {errors.key && (
                  <p className="mt-1 text-sm text-red-600">{errors.key.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('category')}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                />
                {errors.category && (
                  <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Value <span className="text-red-500">*</span>
              </label>
              <textarea
                {...register('value')}
                rows={3}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
              />
              {errors.value && (
                <p className="mt-1 text-sm text-red-600">{errors.value.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                For JSON values, enter a valid JSON string. For simple values, just enter the text.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <input
                type="text"
                {...register('description')}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
              )}
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_public"
                {...register('is_public')}
                className="rounded border-gray-300 text-golf-orange focus:ring-golf-orange"
              />
              <label htmlFor="is_public" className="ml-2 block text-sm text-gray-700">
                Publicly visible
              </label>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={cancelEdit}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-golf-orange rounded-lg hover:bg-golf-orange/90"
              >
                {editingSetting ? 'Update Setting' : 'Add Setting'}
              </button>
            </div>
          </form>
        </div>
      )}

      {state.loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
        </div>
      ) : state.error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {state.error}
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedSettings).map(([category, settings]) => (
            <div key={category} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h3 className="text-lg font-medium text-golf-gray capitalize">{category}</h3>
              </div>
              <div className="divide-y divide-gray-200">
                {settings.map((setting) => (
                  <div key={setting.id} className="px-6 py-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="text-md font-medium text-golf-gray">{setting.key}</h4>
                        {setting.description && (
                          <p className="text-sm text-gray-500 mt-1">{setting.description}</p>
                        )}
                        <div className="mt-2">
                          <pre className="text-sm bg-gray-100 p-2 rounded overflow-x-auto">
                            {typeof setting.value === 'string' 
                              ? setting.value 
                              : JSON.stringify(setting.value, null, 2)}
                          </pre>
                        </div>
                        {setting.is_public && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-2">
                            Public
                          </span>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(setting)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(setting.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}